import React, { useState, useEffect } from "react";
import ReactTagInput from "@pathofdev/react-tag-input";
import "@pathofdev/react-tag-input/build/index.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import imageCompression from "browser-image-compression";
import { db, storage } from "../../firebase";
import { useNavigate, useParams } from "react-router-dom";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import {
  addDoc,
  collection,
  getDoc,
  serverTimestamp,
  doc,
  updateDoc,
} from "firebase/firestore";
import { toast } from "react-toastify";
import Alert from "react-bootstrap/Alert";
import "./editblog.css";

const initialState = {
  title: "",
  tags: [],
  trending: "no",
  category: "",
  description: "",
  comments: [],
  likes: [],
};

const categoryOption = [
  "Technology",
  "Business & Finance",
  "Psychology & Self Improvement",
  "Health & Wellness",
  "Lifestyle",
  "Travel",
  "Food & Cooking",
  "Education & Science",
  "Entertainment & Culture",
  "Sports",
];

const AddEditBlog = ({ user, setActive }) => {
  const [form, setForm] = useState(initialState);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [errorAlert, setErrorAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [originalSize, setOriginalSize] = useState(null);
  const [compressedSize, setCompressedSize] = useState(null);

  const { id } = useParams();

  const navigate = useNavigate();

  const { title, tags, category, trending, description } = form;

  useEffect(() => {
    const uploadFile = () => {
      const storageRef = ref(storage, file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          setProgress(progress);
          switch (snapshot.state) {
            case "paused":
              console.log("Upload is paused");
              break;
            case "running":
              console.log("Upload is running");
              break;
            default:
              break;
          }
        },
        (error) => {
          console.log(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadUrl) => {
            setErrorAlert(false);
            setAlertText("Image upload to firebase successfully");
            toast.info("Image upload to firebase successfully");
            setForm((prev) => ({ ...prev, imgUrl: downloadUrl }));
          });
        }
      );
    };

    file && uploadFile();
  }, [file]);

  useEffect(() => {
    document.title = id
      ? "Edit Blog - youvaraj.com"
      : "Create Blog - youvaraj.com";
    id && getBlogDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    reloadPage();
  });

  const reloadPage = () => {
    if (errorAlert) {
      const interval = setInterval(() => {
        window.location.reload();
      }, 5000);
    }
  };
  const onCloseAlert = () => {
    setAlertText("");
  };
  const getBlogDetail = async () => {
    const docRef = doc(db, "blogs", id);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      setForm({ ...snapshot.data() });
    }
    setActive(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDescriptionChange = (value) => {
    setForm({ ...form, description: value });
  };

  const handleTags = (tags) => {
    setForm({ ...form, tags });
  };

  const handleTrending = (e) => {
    setForm({ ...form, trending: e.target.value });
  };

  const onCategoryChange = (e) => {
    setForm({ ...form, category: e.target.value });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    // Check if it's an image
    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setCompressing(true);
    setOriginalSize((selectedFile.size / 1024 / 1024).toFixed(2)); // Size in MB

    try {
      // Compression options
      const options = {
        maxSizeMB: 1, // Maximum size in MB
        maxWidthOrHeight: 1920, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance
        fileType: selectedFile.type, // Maintain original file type
      };

      // Compress the image
      const compressedFile = await imageCompression(selectedFile, options);

      setCompressedSize((compressedFile.size / 1024 / 1024).toFixed(2)); // Size in MB
      setFile(compressedFile);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);

      // Show success message
      const savedSpace = (
        (selectedFile.size - compressedFile.size) /
        1024 /
        1024
      ).toFixed(2);
      toast.success(`Image compressed! Saved ${savedSpace} MB`);
    } catch (error) {
      console.error("Error compressing image:", error);
      toast.error("Error compressing image. Using original file.");

      // Fallback to original file
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } finally {
      setCompressing(false);
    }
  };

  // Rich text editor modules
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "font",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "blockquote",
    "code-block",
    "link",
    "image",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!title.trim()) {
      toast.error("Please enter a blog title");
      return;
    }
    if (!category) {
      toast.error("Please select a category");
      return;
    }
    if (!tags || tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter blog content");
      return;
    }
    if (!form.imgUrl && !file) {
      toast.error("Please upload a featured image");
      return;
    }

    // If file exists but not uploaded yet, upload it first
    if (file && !form.imgUrl) {
      toast.info("Uploading image, please wait...");
      // Wait for the upload to complete (useEffect will handle it)
      // The imgUrl will be set in the form state by the useEffect
      return;
    }

    // All validations passed and image is uploaded
    if (!id) {
      try {
        await addDoc(collection(db, "blogs"), {
          ...form,
          timestamp: serverTimestamp(),
          author: user.displayName,
          userId: user.uid,
        });
        toast.success("Blog created successfully");
        navigate("/");
      } catch (err) {
        console.log(err);
        toast.error("Error creating blog");
      }
    } else {
      try {
        await updateDoc(doc(db, "blogs", id), {
          ...form,
          timestamp: serverTimestamp(),
          author: user.displayName,
          userId: user.uid,
        });
        toast.success("Blog updated successfully");
        navigate("/");
      } catch (err) {
        console.log(err);
        toast.error("Error updating blog");
      }
    }
  };

  return (
    <div className="containerAddBlog">
      <div className="containerMain">
        <div className="form-header">
          <h2>{id ? "Update Blog Post" : "Create New Blog Post"}</h2>
          <p className="form-subtitle">
            {id
              ? "Edit your blog content"
              : "Share your thoughts with the world"}
          </p>
        </div>

        {alertText && (
          <Alert
            variant={errorAlert ? "danger" : "success"}
            className="custom-alert"
            onClose={onCloseAlert}
            dismissible
          >
            <p>{alertText}</p>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title" className="label">
              <span className="label-icon">📝</span>
              Blog Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={title}
              onChange={handleChange}
              placeholder="Enter an engaging title for your blog..."
              className="form-input textinput"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="category" className="label">
                <span className="label-icon">📂</span>
                Category *
              </label>
              <select
                id="category"
                value={category}
                onChange={onCategoryChange}
                className="form-input textinput"
                required
              >
                <option value="">Select a category</option>
                {categoryOption.map((option, index) => (
                  <option value={option} key={index}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group half-width">
              <label className="label">
                <span className="label-icon">🔥</span>
                Trending Status
              </label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="yes"
                    name="radioOption"
                    checked={trending === "yes"}
                    onChange={handleTrending}
                    className="radio-input"
                  />
                  <span className="radio-text">Yes</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="no"
                    name="radioOption"
                    checked={trending === "no"}
                    onChange={handleTrending}
                    className="radio-input"
                  />
                  <span className="radio-text">No</span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="tags" className="label">
              <span className="label-icon">🏷️</span>
              Tags *
              <span className="info-icon-wrapper">
                <span className="info-icon">ℹ️</span>
                <span className="info-tooltip">
                  <strong>Popular tags:</strong>
                  <br />
                  Lifestyle, Technology, Health & Fitness, Travel, Food &
                  Recipes, Personal Development, Education, Finance & Money,
                  Entertainment, Fashion
                </span>
              </span>
            </label>
            <ReactTagInput
              tags={tags}
              placeholder="Type a tag and press Enter..."
              onChange={handleTags}
            />
            <small className="helper-text">
              Press Enter after typing each tag
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="image" className="label">
              <span className="label-icon">🖼️</span>
              Featured Image *
            </label>
            <div className="image-upload-container">
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                className="file-input"
                required={!id}
                disabled={compressing}
              />
              <label
                htmlFor="image"
                className={`file-input-label ${
                  compressing ? "compressing" : ""
                }`}
              >
                <div className="upload-icon">{compressing ? "⏳" : "📤"}</div>
                <span className="upload-text">
                  {compressing
                    ? "Compressing image..."
                    : imagePreview || form.imgUrl
                    ? "Change Image"
                    : "Choose an image"}
                </span>
                <span className="upload-subtext">
                  {compressing
                    ? "Please wait..."
                    : "PNG, JPG, GIF - Auto-compressed to < 1MB"}
                </span>
              </label>
            </div>

            {compressing && (
              <div className="compression-status">
                <div className="compression-loader"></div>
                <span>Compressing your image for faster upload...</span>
              </div>
            )}

            {compressedSize && !compressing && (
              <div className="compression-info">
                <span className="compression-badge">
                  ✓ Compressed: {originalSize} MB → {compressedSize} MB
                </span>
              </div>
            )}

            {(imagePreview || form.imgUrl) && (
              <div className="image-preview">
                <img src={imagePreview || form.imgUrl} alt="Preview" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description" className="label">
              <span className="label-icon">✍️</span>
              Blog Content *
            </label>
            <div className="rich-text-editor">
              <ReactQuill
                theme="snow"
                value={description}
                onChange={handleDescriptionChange}
                modules={modules}
                formats={formats}
                placeholder="Write your amazing content here..."
              />
            </div>
          </div>

          {progress !== null && progress > 0 && progress < 100 && (
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                >
                  <span className="progress-text">{Math.round(progress)}%</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate("/")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={
                compressing ||
                (progress !== null && progress < 100) ||
                (file && !form.imgUrl)
              }
            >
              {compressing
                ? "Compressing..."
                : progress !== null && progress < 100
                ? "Uploading..."
                : file && !form.imgUrl
                ? "Uploading..."
                : id
                ? "Update Blog"
                : "Publish Blog"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditBlog;
